// useRefusalLine — the ONE state machine behind a write surface's refusal line (ADR-084 §6): what a refused save
// becomes on screen. BUSY says nothing (the door's one toast already did); STALE_WRITE is the stale line with its
// one act, Load the latest, which runs the host's reload; a 404 on an EDIT is the gone line (the row left under the
// surface — a create's 404 is a picked thing, a plain refusal); a surface's own refusal (`OwnRefusal`) by its words;
// anything else is the code's words. The form shell
// composes it (its reload re-seeds the untouched fields and keeps the typing); a staged host (a chain, a terms map)
// calls it itself and hands it to its drawer. The rendering is RefusalLine; the words are errors.json + common:forms.

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STALE_WRITE } from '@ems-app/schemas/common/row-version';
import { apiErrorMessage } from '../../../lib/apiError.js';
import { codeOf, isBusy, statusOf } from '../../../lib/refusal.js';

/** A save the surface refuses ITSELF, in its own words (a scope its own check blocks, an upload part-done) — the line
 *  says them as they are. Any other failure is said by its code, or the generic line: a fault's own English never is. */
export class OwnRefusal extends Error {}

export type RefusalNotice =
  | { kind: 'refused'; message: string }
  | { kind: 'stale'; busy: boolean }
  | { kind: 'gone' }
  | { kind: 'latest' };

export interface RefusalLineState {
  notice: RefusalNotice | null;
  /** Hand a failed write to the line. */
  refuse: (err: unknown) => void;
  /** The stale line's one act: the host re-reads and re-seeds. */
  loadLatest: () => Promise<void>;
  dismiss: () => void;
  /** The stale line can offer its act (the host gave a reload). */
  canReload: boolean;
  /** The row left under the surface: the fields read-only, Save disabled, Close the one act. */
  gone: boolean;
}

export interface RefusalLineOptions {
  /** The surface opened on a row — a 404 then means that row is gone. A create's 404 is a plain refusal. */
  edit: boolean;
  /** Load the latest: re-read and re-seed; resolve true when the latest is showing, false when the row is gone.
   *  Absent, the stale line carries no act. */
  reload?: () => Promise<boolean>;
}

export function useRefusalLine({ edit, reload }: RefusalLineOptions): RefusalLineState {
  const { t: tErrors } = useTranslation('errors');
  const [notice, setNotice] = useState<RefusalNotice | null>(null);
  // Read at call time (an act, never render), so a press sees this render's reload and edit flag.
  const optsRef = useRef({ edit, reload });
  useLayoutEffect(() => {
    optsRef.current = { edit, reload };
  });

  const refuse = useCallback(
    (err: unknown) => {
      if (isBusy(err)) return;
      if (codeOf(err) === STALE_WRITE) {
        setNotice({ kind: 'stale', busy: false });
        return;
      }
      if (statusOf(err) === 404 && optsRef.current.edit) {
        setNotice({ kind: 'gone' });
        return;
      }
      setNotice({ kind: 'refused', message: err instanceof OwnRefusal ? err.message : apiErrorMessage(err, tErrors) });
    },
    [tErrors],
  );

  const loadLatest = useCallback(async () => {
    const { reload: run } = optsRef.current;
    if (!run) return;
    setNotice({ kind: 'stale', busy: true });
    try {
      setNotice((await run()) ? { kind: 'latest' } : { kind: 'gone' });
    } catch {
      // The row is still stale and the act is the only way on: the line stays, for another press (a busy answer
      // was said by the door; a transport failure says nothing more — the press is cheap).
      setNotice({ kind: 'stale', busy: false });
    }
  }, []);

  const dismiss = useCallback(() => setNotice(null), []);

  return { notice, refuse, loadLatest, dismiss, canReload: reload !== undefined, gone: notice?.kind === 'gone' };
}
